import {
  ApprovalForm,
  type ApprovalFormFields,
} from "@/components/ApprovalForm/ApprovalForm";
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from "@/components/ui/buttons/PrimaryButton";
import { BLACKLIST_APPLY_TYPE } from "@/constants";
import { useModal } from "@/hooks/modals/useModal";
import { useFlow } from "@/hooks/services/useFlow";
import type {
  SrmSupplierBlacklistSchema,
  SupplierBlacklistItemDetail,
  SupplierUserSimpleListItem,
} from "@/types/apis";
import { notifySuccess } from "@/utils/notification";
import type { FormInstance } from "antd";
import type { BlacklistApplyFormFields } from "../type";

import { useBlacklistRecordDetail } from "./useBlacklistRecordDetail";

export function useFormFooter(
  form: FormInstance<BlacklistApplyFormFields>,
  cleanup: () => void,
) {
  const { props, open } = useModal("SUPPLIER_BLACK_LIST");
  const {
    mode,
    taskId,
    recordId,
    onSuccess,
    supplier,
    flowStatus,
    viewStatus,
    isReadOnly,
  } = props;

  const handleSuccess = (message: string) => {
    notifySuccess(message);
    onSuccess?.();
    cleanup();
  };

  const detail = useBlacklistRecordDetail(recordId);

  const createFlowCodeRules = [!detail, flowStatus !== "审批中"];

  const shouldCreateFlowCode = createFlowCodeRules.some((rule) => rule);

  const { flowCode, startFlowMutation, passFlowMutation } = useFlow(
    "SUPPLIER_BLACK_LIST",
    open && shouldCreateFlowCode,
  );

  // 移入申请
  const submitAddTo = async () => {
    if (!flowCode || !supplier) return;

    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "SUPPLIER_BLACK_LIST",
        formNo: flowCode,
        variable: {
          supplierUserId: supplier.id,
          supplierUserName: supplier.supplierName,
        },
        srmSupplierBlacklistDTO: buildAddToDto(supplier, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess("移入申请已提交"),
      },
    );
  };

  // 重新提交移入申请
  const resubmitAddTo = async () => {
    if (!flowCode || !detail) return;

    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "SUPPLIER_BLACK_LIST",
        formNo: flowCode,
        variable: {
          supplierUserId: detail.supplierUserId,
          supplierUserName: detail.supplierUserName,
        },
        srmSupplierBlacklistDTO: buildAddToDto(detail, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess("移入申请已提交"),
      },
    );
  };

  // 移出申请
  const submitMoveFrom = async () => {
    if (!flowCode || !detail) return;
    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "SUPPLIER_BLACK_LIST",
        formNo: flowCode,
        variable: {
          supplierUserId: detail.supplierUserId,
          supplierUserName: detail.supplierUserName,
        },
        srmSupplierBlacklistDTO: buildRemoveFromDto(detail, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess("移出申请已提交"),
      },
    );
  };

  // 重新发起移出申请
  const resubmitMoveFrom = async () => {
    if (!flowCode || !detail) return;

    const values = await form.validateFields();
    startFlowMutation.mutate(
      {
        flowCode: "SUPPLIER_BLACK_LIST",
        formNo: flowCode,
        variable: {
          supplierUserId: detail.supplierUserId,
          supplierUserName: detail.supplierUserName,
        },
        srmSupplierBlacklistDTO: buildRemoveFromDto(detail, flowCode, values),
      },
      {
        onSuccess: () => handleSuccess("移出申请已提交"),
      },
    );
  };

  // 审批
  const handleApprove = (values: ApprovalFormFields) => {
    if (!taskId) return;

    passFlowMutation.mutate(
      {
        taskId: taskId,
        flowParamsDto: {
          skipType: values.skipType === "REJECT" ? "" : "PASS", // 驳回时传空处理
          message: values.message,
          flowCode: "SUPPLIER_BLACK_LIST",
          variable: {},
        },
      },
      {
        onSuccess: () => handleSuccess("审批成功"),
      },
    );
  };

  const renderFooter = (detail?: SupplierBlacklistItemDetail) => {
    const Footer = {
      addTo: <PrimaryButton onClick={submitAddTo}>提交</PrimaryButton>,
      reAddTo: <PrimaryButton onClick={resubmitAddTo}>提交</PrimaryButton>,
      moveFrom: <PrimaryButton onClick={submitMoveFrom}>提交</PrimaryButton>,
      reMoveFrom: (
        <PrimaryButton onClick={resubmitMoveFrom}>提交</PrimaryButton>
      ),
      close: (
        <PrimaryOutlineButton onClick={cleanup}>关闭</PrimaryOutlineButton>
      ),
      approve: <ApprovalForm onApprovalSubmit={handleApprove} />,
    };

    if (isReadOnly) return Footer.close;

    // 用户收到申请并审批(待办任务)
    if (mode === "inReview") {
      return viewStatus ? Footer.approve : Footer.close;
    }

    // 提交冻结申请
    if (!detail) return Footer.addTo;

    // 用户发起申请
    const rules = [
      {
        // 重新提交移入申请
        match:
          detail.applyType === BLACKLIST_APPLY_TYPE.ADD_TO &&
          flowStatus === "已驳回",
        render: Footer.reAddTo,
      },
      {
        // 提交移出申请
        match:
          detail.applyType === BLACKLIST_APPLY_TYPE.ADD_TO &&
          flowStatus === "已通过",
        render: Footer.moveFrom,
      },
      {
        // 重新提交移出申请
        match:
          detail.applyType === BLACKLIST_APPLY_TYPE.REMOVE_FROM &&
          flowStatus === "已驳回",
        render: Footer.reMoveFrom,
      },
    ];

    const rule = rules.find((r) => r.match);
    return rule ? rule.render : Footer.close;
  };

  return renderFooter;
}

function buildAddToDto(
  base: SupplierUserSimpleListItem | SupplierBlacklistItemDetail,
  flowCode: string,
  values: BlacklistApplyFormFields,
): SrmSupplierBlacklistSchema {
  return {
    processCode: flowCode,
    supplierUserId: "supplierName" in base ? base.id : base.supplierUserId,
    supplierCode: base.supplierCode,
    supplierUserName:
      "supplierName" in base ? base.supplierName : base.supplierUserName,
    supplierUsUserName:
      "supplierName" in base ? base.supplierUsName : base.supplierUsUserName,
    fileIds: values.files?.map((item) => item.uid),
    blacklistReason: values.blacklistReason,
    applyType: BLACKLIST_APPLY_TYPE.ADD_TO,
  };
}

function buildRemoveFromDto(
  detail: SupplierBlacklistItemDetail,
  flowCode: string,
  values: BlacklistApplyFormFields,
): SrmSupplierBlacklistSchema {
  return {
    processCode: flowCode,
    supplierUserId: detail.supplierUserId,
    supplierCode: detail.supplierCode,
    supplierUserName: detail.supplierUserName,
    supplierUsUserName: detail.supplierUsUserName,
    fileIds: detail.srmSupplierFlowBusinessFiles.map((item) =>
      item.id.toString(),
    ),
    blacklistReason: detail.blacklistReason,
    removeBlacklistReason: values.removeBlacklistReason,
    applyType: BLACKLIST_APPLY_TYPE.REMOVE_FROM,
  };
}
